import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  EMPTY_DRAFT, validateDraft, buildCharacter, type CharacterDraft,
} from '@shared/rules/build'
import { CLASS_BUILDS_BY_ID } from '@shared/rules/data/classes'
import { magicInitiateList } from '@shared/rules/build'
import { ApiError, api } from '@/lib/api'
import { draftToSheetData } from '@/lib/pdf/fromBuild'
import { downloadCharacterSheet } from '@/lib/pdf/fillSheet'
import { Button, Panel } from '@/components/ui'
import { cn } from '@/lib/cn'
import {
  StepBackground, StepClass, StepSpecies, StepWelcome, type StepProps,
} from '@/components/wizard/StepIdentity'
import { StepAbilities, StepSkills } from '@/components/wizard/StepNumbers'
import { StepFeatures } from '@/components/wizard/StepFeatures'
import { StepEquipment, StepSpells } from '@/components/wizard/StepGear'
import { StepDetails, StepReview } from '@/components/wizard/StepFinish'

interface StepDef {
  id: string
  label: string
  /** Short label for the mobile progress strip. */
  short: string
}

const BASE_STEPS: StepDef[] = [
  { id: 'welcome', label: 'Getting started', short: 'Start' },
  { id: 'class', label: 'Class', short: 'Class' },
  { id: 'species', label: 'Species', short: 'Species' },
  { id: 'background', label: 'Background', short: 'Background' },
  { id: 'abilities', label: 'Ability scores', short: 'Scores' },
  { id: 'skills', label: 'Skills', short: 'Skills' },
  { id: 'features', label: 'Features & feats', short: 'Features' },
  { id: 'equipment', label: 'Equipment', short: 'Gear' },
  { id: 'spells', label: 'Spells', short: 'Spells' },
  { id: 'details', label: 'Name & description', short: 'Details' },
  { id: 'review', label: 'Review & finish', short: 'Finish' },
]

const storageKey = (campaignId: string) => `campaign-hub:draft:${campaignId}`

export default function CreateCharacter() {
  const { campaignId = '' } = useParams()
  const navigate = useNavigate()

  const [draft, setDraft] = useState<CharacterDraft>(() => loadDraft(campaignId))
  const [stepId, setStepId] = useState('welcome')
  const [creating, setCreating] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [createdId, setCreatedId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Keep the draft through an accidental refresh; clear it once created.
  useEffect(() => {
    if (createdId) return
    try {
      localStorage.setItem(storageKey(campaignId), JSON.stringify(draft))
    } catch {
      // Private browsing or a full quota — the wizard still works in-session.
    }
  }, [draft, campaignId, createdId])

  const update = useCallback((patch: Partial<CharacterDraft>) => {
    setDraft((current) => ({ ...current, ...patch }))
  }, [])

  // The spells step only exists for characters that actually get spells.
  const steps = useMemo(() => {
    const classBuild = draft.classId ? CLASS_BUILDS_BY_ID.get(draft.classId) : null
    const castsFromClass = Boolean(classBuild?.spellcasting)
    return BASE_STEPS.filter((step) => step.id !== 'spells' || castsFromClass)
  }, [draft.classId])

  const index = Math.max(0, steps.findIndex((step) => step.id === stepId))
  const current = steps[index] ?? steps[0]!
  const issues = useMemo(() => validateDraft(draft), [draft])
  const issuesByStep = useMemo(() => {
    const map = new Map<string, number>()
    for (const issue of issues) map.set(issue.step, (map.get(issue.step) ?? 0) + 1)
    return map
  }, [issues])

  // Falling off the end of the list (e.g. class changed away from a caster).
  useEffect(() => {
    if (!steps.some((step) => step.id === stepId)) setStepId(steps[0]!.id)
  }, [steps, stepId])

  const goTo = (id: string) => {
    setStepId(id)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const stepProps: StepProps = { draft, update }

  async function handleDownload() {
    setDownloading(true)
    setError(null)
    try {
      const built = buildCharacter(draft)
      if (!built) throw new Error('The character is not finished yet')
      await downloadCharacterSheet(draftToSheetData(draft, built))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not build the PDF')
    } finally {
      setDownloading(false)
    }
  }

  async function handleCreate() {
    setCreating(true)
    setError(null)
    try {
      const res = await api.createFromDraft(campaignId, draft)
      setCreatedId(res.character.id)
      try {
        localStorage.removeItem(storageKey(campaignId))
      } catch { /* nothing to clean up */ }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create the character')
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[13rem_minmax(0,1fr)]">
      <StepNav steps={steps} currentId={current.id} issuesByStep={issuesByStep} onSelect={goTo} />

      <div className="min-w-0">
        {current.id === 'welcome' && <StepWelcome />}
        {current.id === 'class' && <StepClass {...stepProps} />}
        {current.id === 'species' && <StepSpecies {...stepProps} />}
        {current.id === 'background' && <StepBackground {...stepProps} />}
        {current.id === 'abilities' && <StepAbilities {...stepProps} />}
        {current.id === 'skills' && <StepSkills {...stepProps} />}
        {current.id === 'features' && <StepFeatures {...stepProps} />}
        {current.id === 'equipment' && <StepEquipment {...stepProps} />}
        {current.id === 'spells' && <StepSpells {...stepProps} />}
        {current.id === 'details' && <StepDetails {...stepProps} />}
        {current.id === 'review' && (
          <StepReview
            {...stepProps}
            issues={issues}
            onGoToStep={goTo}
            onCreate={handleCreate}
            onDownload={handleDownload}
            creating={creating}
            downloading={downloading}
            createdId={createdId}
            error={error}
          />
        )}

        <div className="mt-8 flex items-center justify-between gap-3 border-t border-ink-700/60 pt-5">
          <Button
            variant="ghost"
            disabled={index === 0}
            onClick={() => goTo(steps[index - 1]!.id)}
          >
            ← Back
          </Button>

          <p className="text-xs text-ink-500">
            Step {index + 1} of {steps.length}
          </p>

          {createdId ? (
            <Button variant="primary" onClick={() => navigate(`/characters/${createdId}`)}>
              Open character sheet →
            </Button>
          ) : (
            <Button
              variant="primary"
              disabled={index === steps.length - 1}
              onClick={() => goTo(steps[index + 1]!.id)}
            >
              Next →
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

function StepNav({
  steps, currentId, issuesByStep, onSelect,
}: {
  steps: StepDef[]
  currentId: string
  issuesByStep: Map<string, number>
  onSelect: (id: string) => void
}) {
  const currentIndex = steps.findIndex((step) => step.id === currentId)

  return (
    <nav aria-label="Character creation steps" className="lg:sticky lg:top-20 lg:self-start">
      {/* Mobile: a compact scrolling strip. */}
      <ol className="-mx-4 flex gap-1.5 overflow-x-auto px-4 pb-2 lg:hidden">
        {steps.map((step, i) => (
          <li key={step.id}>
            <button
              type="button"
              onClick={() => onSelect(step.id)}
              aria-current={step.id === currentId ? 'step' : undefined}
              className={cn(
                'whitespace-nowrap rounded-full border px-3 py-1.5 text-[13px] transition-colors',
                step.id === currentId
                  ? 'border-gold-500 bg-gold-900/40 text-gold-400'
                  : i < currentIndex
                    ? 'border-ink-600 text-ink-300'
                    : 'border-ink-700 text-ink-500',
              )}
            >
              {step.short}
            </button>
          </li>
        ))}
      </ol>

      {/* Desktop: a full vertical list with outstanding-choice counts. */}
      <Panel className="hidden p-2 lg:block">
        <ol className="space-y-0.5">
          {steps.map((step, i) => {
            const outstanding = issuesByStep.get(step.id) ?? 0
            const active = step.id === currentId
            return (
              <li key={step.id}>
                <button
                  type="button"
                  onClick={() => onSelect(step.id)}
                  aria-current={active ? 'step' : undefined}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors',
                    active ? 'bg-gold-900/40 text-gold-400' : 'text-ink-400 hover:bg-ink-800 hover:text-ink-200',
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      'grid size-5 shrink-0 place-items-center rounded-full border text-[10px] font-mono',
                      active ? 'border-gold-500 text-gold-400'
                        : i < currentIndex ? 'border-ink-500 text-ink-400' : 'border-ink-700 text-ink-600',
                    )}
                  >
                    {i + 1}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{step.label}</span>
                  {outstanding > 0 && (
                    <span
                      title={`${outstanding} choice${outstanding === 1 ? '' : 's'} left`}
                      className="size-1.5 shrink-0 rounded-full bg-gold-500"
                    />
                  )}
                </button>
              </li>
            )
          })}
        </ol>
      </Panel>
    </nav>
  )
}

function loadDraft(campaignId: string): CharacterDraft {
  try {
    const saved = localStorage.getItem(storageKey(campaignId))
    if (!saved) return { ...EMPTY_DRAFT }
    // Merge over the defaults so a draft saved before a field existed still loads.
    return { ...EMPTY_DRAFT, ...(JSON.parse(saved) as Partial<CharacterDraft>) }
  } catch {
    return { ...EMPTY_DRAFT }
  }
}

export { magicInitiateList }
